// DepthDeltaVisualizer v3 — the PRODUCT placement gauge.
//
// v1 (rings) and v2 (three diagnostic ground lines) earned their keep
// during the 2026-08-20 LUAT rounds; the 2026-08-21 daylight car test
// confirmed the flow: when the gauge sits under the wheel face on the
// car, depth placement is near perfect. v3 is the customer-facing cut:
//
//   - GREEN line only — the depth-measured placement line on the floor
//     (blue plane-line and white truth-tick were engineering aids).
//   - Thicker (0.018m vs the diagnostic 0.012m) — it's now a primary
//     aiming affordance, not a debug overlay.
//   - SMOOTHED: new readings tween exponentially (tau 100ms ⇒ ~95%
//     converged at 300ms) instead of snapping, and a depth dropout
//     HOLDS the last pose instead of blinking out. Deliberate
//     smoothness-over-latency trade: the raw depth stream is sporadic
//     (info-null bursts, edge-locking) and a jittery line reads as
//     broken even when placement is fine.
//
// Mounted whenever depth placement is active (the default); the
// diagnostic text HUD stays behind ?depthHud.

import * as THREE from 'three'
import { GAUGE_RENDER_ORDER } from '@/ar/wheel-render-order'
import { DEPTH_TWEEN_TAU_MS } from '@/ar/depth-placement'
import type { HitResult } from '@/ar/wheel-renderer'

const LINE_LENGTH_M = 1.0
export const GAUGE_LINE_THICKNESS_M = 0.018
const LINE_HEIGHT_M = 0.006
/** Tiny lift off the floor so the line never z-fights the tiles. */
const FLOOR_EPSILON_M = 0.003

/** Exponential smoothing time-constant — shared with the preview
 *  wheel tween so line and wheel glide in lockstep. */
export const GAUGE_TWEEN_TAU_MS = DEPTH_TWEEN_TAU_MS

/** Gauge green — single source for the 3D line AND any UI copy that
 *  names it (the aim-hint chip colors the words "green line" with it). */
export const GAUGE_LINE_COLOR_HEX = '#73ff8b'
const DEPTH_LINE_COLOR = 0x73ff8b // keep in sync with GAUGE_LINE_COLOR_HEX

function buildLine(name: string, color: number): THREE.Group {
	const group = new THREE.Group()
	group.name = name
	group.visible = false
	const mesh = new THREE.Mesh(
		// Length along local X; the group is yawed so X is perpendicular
		// to the camera→hit azimuth (a "distance tick" across the aim).
		new THREE.BoxGeometry(LINE_LENGTH_M, LINE_HEIGHT_M, GAUGE_LINE_THICKNESS_M),
		new THREE.MeshBasicMaterial({
			color,
			depthTest: false,
			transparent: true,
			opacity: 0.9,
		}),
	)
	mesh.name = 'depth-delta-line-mesh'
	// Above every wheel, always — see GAUGE_RENDER_ORDER.
	mesh.renderOrder = GAUGE_RENDER_ORDER
	group.add(mesh)
	return group
}

export class DepthDeltaVisualizer {
	private readonly scene: THREE.Scene
	private readonly camera: THREE.Camera
	private readonly getDepthHit: () => HitResult | null
	private readonly getFloorY: () => number | null
	private readonly getAimingActive: () => boolean
	private readonly now: () => number
	private readonly depthLine: THREE.Group
	/** Tween target — survives dropouts (hold-last). null until the
	 *  first reading; the line stays hidden until then. */
	private target: { position: THREE.Vector3; quaternion: THREE.Quaternion } | null =
		null
	private lastUpdateMs: number | null = null

	constructor({
		scene,
		camera,
		getDepthHit,
		getFloorY,
		getAimingActive,
		now,
	}: {
		scene: THREE.Scene
		camera: THREE.Camera
		getDepthHit: () => HitResult | null
		getFloorY: () => number | null
		/** Is the user AIMING (preview active)? The gauge belongs to the
		 *  aiming state only — a placed wheel hides it until the next
		 *  placement begins. Defaults to always-aiming. */
		getAimingActive?: () => boolean
		/** Injectable clock for tests; defaults to performance.now. */
		now?: () => number
	}) {
		this.scene = scene
		this.camera = camera
		this.getDepthHit = getDepthHit
		this.getFloorY = getFloorY
		this.getAimingActive = getAimingActive ?? (() => true)
		this.now = now ?? (() => performance.now())
		this.depthLine = buildLine('depth-delta-depth-line', DEPTH_LINE_COLOR)
		scene.add(this.depthLine)
	}

	/** Per-frame refresh: fold any fresh reading into the tween target
	 *  (dropouts keep the previous target — hold-last), then ease the
	 *  line toward it. */
	update(): void {
		// Aiming gate: once a wheel is placed the gauge disappears and the
		// tween resets — the next aiming session snaps fresh instead of
		// gliding in from a stale pose.
		if (!this.getAimingActive()) {
			this.depthLine.visible = false
			this.target = null
			this.lastUpdateMs = null
			return
		}
		const hit = this.getDepthHit()
		if (hit != null) {
			const floorY = this.getFloorY() ?? hit.position.y
			const position = new THREE.Vector3(
				hit.position.x,
				floorY + FLOOR_EPSILON_M,
				hit.position.z,
			)
			// Yaw the line perpendicular to the horizontal camera→hit
			// azimuth; pure Y-rotation keeps it flat on the floor.
			const camPos = this.camera.getWorldPosition(new THREE.Vector3())
			const azimuth = new THREE.Vector3(
				hit.position.x - camPos.x,
				0,
				hit.position.z - camPos.z,
			)
			const quaternion = new THREE.Quaternion()
			if (azimuth.lengthSq() > 1e-6) {
				azimuth.normalize()
				quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), azimuth)
			}
			this.target = { position, quaternion }
		}
		// No reading EVER → nothing to show yet.
		if (this.target == null) {
			this.depthLine.visible = false
			this.lastUpdateMs = this.now()
			return
		}
		const nowMs = this.now()
		const dtMs = this.lastUpdateMs == null ? 0 : nowMs - this.lastUpdateMs
		this.lastUpdateMs = nowMs
		if (!this.depthLine.visible) {
			// First reading: snap — there is no meaningful pose to tween from.
			this.depthLine.visible = true
			this.depthLine.position.copy(this.target.position)
			this.depthLine.quaternion.copy(this.target.quaternion)
			return
		}
		const alpha = 1 - Math.exp(-dtMs / GAUGE_TWEEN_TAU_MS)
		this.depthLine.position.lerp(this.target.position, alpha)
		this.depthLine.quaternion.slerp(this.target.quaternion, alpha)
	}

	dispose(): void {
		this.scene.remove(this.depthLine)
		this.depthLine.traverse((obj) => {
			if (obj instanceof THREE.Mesh) {
				obj.geometry.dispose()
				;(obj.material as THREE.Material).dispose()
			}
		})
	}
}
